import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Seo from './Seo';

const technologies = [
  { name: 'SAP', route: '/technologies/sap', desc: 'Enterprise planning, process standardization, and SAP transformation support for scalable operations.' },
  { name: 'Cloud Computing (AWS + Azure)', route: '/technologies/cloud-computing', desc: 'Unified cloud transformation services covering AWS and Azure architecture, migration, governance, and optimization.' },
  { name: 'Big Data Analytics', route: '/technologies/big-data', desc: 'Scalable data engineering and analytics platforms that enable trusted enterprise decision intelligence.' },
  { name: 'Digital Analytics', route: '/technologies/digital-analytics', desc: 'Digital measurement, funnel analysis, and KPI insights to improve cross-channel performance.' },
  { name: 'Cyber Security', route: '/technologies/cybersecurity', desc: 'Security posture improvement, governance controls, and operational resilience across critical systems.' },
  { name: 'AI (Artificial Intelligence)', route: '/technologies/ai', desc: 'AI strategy, model engineering, and deployment governance for measurable business outcomes.' },
  { name: 'Testing', route: '/technologies/testing', desc: 'Quality engineering and test automation services to improve release confidence and product stability.' },
];

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7 } } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

const TechnologiesOverview = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-slate-50 min-h-screen pt-6 pb-20 [&_.text-h1]:text-h2 [&_.text-h2]:text-h3 [&_.text-body]:text-small [&_.text-small]:text-subtext">
      <Seo
        title="Technology Practice"
        description="Libsys technology practice: SAP, AWS & Azure cloud, big data analytics, digital analytics, cybersecurity, AI, and quality engineering — for enterprise transformation."
        path="/technologies"
      />
      <section className="max-w-5xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          variants={stagger}
          className="text-center mx-auto mb-16"
        >
          <motion.span variants={fadeUp} className="text-red-tagline block mb-1">
            Technology Practice
          </motion.span>
          <motion.h1 variants={fadeUp} className="text-h2 font-black text-primary tracking-tight leading-tight mb-1">
            Enterprise Technology Capabilities
          </motion.h1>
          <motion.p variants={fadeUp} className="text-slate-600 max-w-2xl mx-auto font-medium leading-relaxed text-small">
            We design and deliver modern technology programs with governance, scalability, and measurable business impact built into every phase.
          </motion.p>
        </motion.div>

        <div className="bg-white border border-slate-200 rounded-2xl px-4 lg:px-6">
          {technologies.map((tech, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="py-5 lg:py-6 border-b border-slate-200 last:border-b-0"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
                <div className="lg:col-span-1">
                  <p className="text-primary/60 text-xs font-black tracking-[0.22em]">T{String(i + 1).padStart(2, '0')}</p>
                </div>
                <div className="lg:col-span-8">
                  <h3 className="text-h4 font-black text-primary leading-tight mb-1">{tech.name}</h3>
                  <p className="text-slate-600 text-body leading-relaxed font-medium">{tech.desc}</p>
                </div>
                <div className="lg:col-span-3 lg:text-right">
                  <Link to={tech.route} className="inline-flex items-center gap-1.5 text-primary font-black text-tiny uppercase tracking-[0.14em] hover:gap-2 transition-all">
                    Open Detail <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-5 lg:p-6">
          <h3 className="text-primary text-h4 font-black mb-4">How We Engage</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: 'Assess', desc: 'Current-state review, architecture analysis, and risk baseline definition.' },
              { title: 'Implement', desc: 'Phased execution with integration governance and quality checkpoints.' },
              { title: 'Optimize', desc: 'Continuous tuning for performance, resilience, and measurable outcomes.' }
            ].map((item) => (
              <div key={item.title} className="rounded-xl bg-slate-50/80 border border-slate-200 p-4">
                <p className="text-tiny font-black uppercase tracking-[0.2em] text-slate-500 mb-2">{item.title}</p>
                <p className="text-slate-600 text-subtext font-medium leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TechnologiesOverview;
